import DOMPurify from "dompurify";
import type { ParsedScript, ScriptWord } from "../types";
import { flattenTextSizes, normalizeTextSize } from "./text-size";

/** Presentation-only HTML. No links, embeds, images, forms, event handlers or arbitrary CSS. */
export function sanitizeScript(html: string): string {
  // Nonprinting C0 controls have no script meaning and expand sixfold in JSON.
  // Keep normal whitespace while ensuring accepted text fits the live relay.
  const clean = DOMPurify.sanitize(
    html
      .slice(0, 2_000_000)
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ""),
    {
      ALLOWED_TAGS: [
        "p",
        "div",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "s",
        "span",
        "h1",
        "h2",
        "h3",
        "blockquote",
        "ul",
        "ol",
        "li",
        "hr",
        "sub",
        "sup",
      ],
      ALLOWED_ATTR: ["style", "dir"],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
    },
  );
  const root = document.createElement("div");
  root.innerHTML = clean;
  const textSizes = new Map<HTMLElement, string>();
  for (const el of root.querySelectorAll<HTMLElement>("[style]")) {
    const size = normalizeTextSize(el.style.getPropertyValue("font-size"));
    if (size) textSizes.set(el, size);
    const declarations: string[] = [];
    for (const property of [
      "color",
      "background-color",
      "text-align",
      "font-weight",
      "font-style",
      "text-decoration",
      "direction",
    ]) {
      const value = el.style.getPropertyValue(property).trim();
      if (!value || /url|expression|var\(|attr\(|[\\<>]/i.test(value)) continue;
      const allowed = property.endsWith("color")
        ? /^(#[a-f\d]{3,8}|[a-z]{1,24}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\))$/i.test(
            value,
          )
        : property === "text-align"
          ? /^(left|center|right|justify|start|end)$/.test(value)
          : property === "font-weight"
            ? /^(normal|bold|[1-9]00)$/.test(value)
            : property === "font-style"
              ? /^(normal|italic|oblique)$/.test(value)
              : property === "direction"
                ? /^(ltr|rtl)$/.test(value)
                : /^(none|underline|line-through|underline line-through)$/.test(
                    value,
                  );
      if (allowed) declarations.push(`${property}: ${value}`);
    }
    el.removeAttribute("style");
    if (declarations.length) el.setAttribute("style", declarations.join("; "));
  }
  flattenTextSizes(root, textSizes);
  for (const el of root.querySelectorAll("[dir]"))
    if (!/^(ltr|rtl|auto)$/.test(el.getAttribute("dir") || ""))
      el.removeAttribute("dir");
  return root.innerHTML;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
export function plainTextToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>") || "<br>"}</p>`)
    .join("");
}

export function normalizeWord(word: string): string {
  return word
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase()
    .replace(/ß/g, "ss")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .replace(/[’'`]/g, "")
    .replace(/[^\p{L}\p{N}]/gu, "");
}

/** Whitespace words for spaced scripts; individual CJK characters permit incremental following without spaces. */
export function textTokens(
  text: string,
): { text: string; start: number; end: number }[] {
  return [
    ...text.matchAll(
      /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]\p{M}*[。、！？]*|[^\s\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]+/gu,
    ),
  ].map((m) => ({ text: m[0], start: m.index!, end: m.index! + m[0].length }));
}

type TextPiece = { node: Text; start: number; end: number };
const blockTags = new Set(["P", "DIV", "H1", "H2", "H3", "BLOCKQUOTE", "LI"]);
function analyze(html: string) {
  const root = document.createElement("div");
  root.innerHTML = sanitizeScript(html);
  let text = "";
  const pieces: TextPiece[] = [];
  const blockRanges: { node: HTMLElement; start: number; end: number }[] = [];
  function walk(node: Node) {
    if (node.nodeType === 3) {
      const start = text.length;
      text += node.textContent || "";
      pieces.push({ node: node as Text, start, end: text.length });
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node as HTMLElement;
    if (el.tagName === "BR" || el.tagName === "HR") {
      text += "\n";
      return;
    }
    const isBlock = blockTags.has(el.tagName);
    if (isBlock && text && !text.endsWith("\n")) text += "\n";
    const start = text.length;
    for (const child of [...node.childNodes]) walk(child);
    if (isBlock) {
      blockRanges.push({ node: el, start, end: text.length });
      text += "\n";
    }
  }
  for (const node of [...root.childNodes]) walk(node);
  // Every bracketed production instruction is visible but omitted from recognition.
  const cueRanges = [...text.matchAll(/\[\[[^\]\n]+\]\]|\[[^\]\n]+\]/g)].map(
    (m) => ({ start: m.index!, end: m.index! + m[0].length, title: m[0] }),
  );
  const regions: { start: number; end: number; cue: boolean }[] = [];
  let offset = 0;
  for (const c of cueRanges) {
    regions.push(
      { start: offset, end: c.start, cue: false },
      { start: c.start, end: c.end, cue: true },
    );
    offset = c.end;
  }
  regions.push({ start: offset, end: text.length, cue: false });
  const words: ScriptWord[] = [];
  for (const region of regions)
    for (const token of textTokens(text.slice(region.start, region.end)))
      words.push({
        index: words.length,
        text: token.text,
        normalized: normalizeWord(token.text),
        start: region.start + token.start,
        end: region.start + token.end,
        cue: region.cue,
      });
  return { root, text, pieces, blockRanges, cueRanges, words };
}

export function parseScript(html: string): ParsedScript {
  const a = analyze(html);
  const firstAfter = (position: number, field: "start" | "end") => {
    let low = 0,
      high = a.words.length;
    while (low < high) {
      const mid = (low + high) >>> 1;
      if (a.words[mid][field] <= position) low = mid + 1;
      else high = mid;
    }
    return low;
  };
  const blocks = a.blockRanges
    .sort((x, y) => x.start - y.start)
    .map((b, index) => ({
      index,
      html: b.node.outerHTML,
      wordStart: firstAfter(b.start, "end"),
      wordEnd: firstAfter(b.end - 1, "start") - 1,
    }));
  const markers = a.blockRanges
    .filter((b) => /^H[123]$/.test(b.node.tagName))
    .map((b) => ({
      title: b.node.textContent || "Section",
      wordIndex: Math.max(
        0,
        a.words.findIndex((w) => w.start >= b.start),
      ),
    }));
  for (const c of a.cueRanges.filter((c) => c.title.startsWith("[[")))
    markers.push({
      title: c.title.slice(2, -2),
      wordIndex: Math.max(
        0,
        a.words.findIndex((w) => w.start >= c.start),
      ),
    });
  for (const c of a.cueRanges.filter((c) => /^\[(cue|marker):/i.test(c.title)))
    markers.push({
      title: c.title.replace(/^\[(cue|marker):\s*/i, "").slice(0, -1),
      wordIndex: Math.max(
        0,
        a.words.findIndex((w) => w.start >= c.start),
      ),
    });
  return {
    text: a.text.trimEnd(),
    words: a.words,
    blocks,
    markers: markers.sort((x, y) => x.wordIndex - y.wordIndex),
  };
}

/** Indexes derive from flattened text; a word split by formatting has multiple spans sharing its index. */
export function tokenizeHtml(html: string): string {
  const a = analyze(html);
  let cursor = 0;
  for (const piece of a.pieces) {
    while (cursor < a.words.length && a.words[cursor].end <= piece.start)
      cursor++;
    const fragment = document.createDocumentFragment();
    let offset = piece.start;
    let wi = cursor;
    while (wi < a.words.length && a.words[wi].start < piece.end) {
      const word = a.words[wi];
      const start = Math.max(word.start, piece.start),
        end = Math.min(word.end, piece.end);
      if (start > offset)
        fragment.append(document.createTextNode(a.text.slice(offset, start)));
      const span = document.createElement("span");
      span.dataset.wordIndex = String(word.index);
      span.className = `prompt-word${word.cue ? " cue-word" : ""}`;
      if (word.cue) span.dataset.cue = "true";
      span.textContent = a.text.slice(start, end);
      fragment.append(span);
      offset = end;
      wi++;
    }
    if (offset < piece.end)
      fragment.append(document.createTextNode(a.text.slice(offset, piece.end)));
    piece.node.replaceWith(fragment);
  }
  return a.root.innerHTML;
}

export function renderPrompterHtml(html: string, activeWord: number): string {
  const root = document.createElement("div");
  root.innerHTML = tokenizeHtml(html);
  for (const el of root.querySelectorAll<HTMLElement>("[data-word-index]")) {
    const index = Number(el.dataset.wordIndex);
    el.classList.add(
      index === activeWord ? "active" : index < activeWord ? "past" : "future",
    );
  }
  return root.innerHTML;
}

export function scriptStats(html: string, wpm = 140) {
  const parsed = parseScript(html);
  const spokenWords = parsed.words.filter((w) => !w.cue && w.normalized).length;
  return {
    words: parsed.words.length,
    spokenWords,
    characters: parsed.text.length,
    seconds: Math.ceil((spokenWords / Math.max(1, wpm)) * 60),
    markers: parsed.markers.length,
  };
}
